import { Box } from "@mui/material";

import { useIsMobile } from "../AppLayout/useResponsive";
import { IconButton } from "./IconButton";
import { Text } from "./Text";
import { useTu } from "@src/appV2/i18n/useTu";

export interface DialogHeaderProps {
  /**
   * The title text displayed in the header
   */
  title: string;
  /**
   * Whether to show the close button
   */
  showCloseButton?: boolean;
  /**
   * Callback when close button is clicked
   */
  onClose?: () => void;
}

/**
 * Dialog header component with title and optional close button.
 * Used internally by Dialog component.
 */
export function DialogHeader(props: DialogHeaderProps) {
  const { tu } = useTu("common");
  const { title, showCloseButton = true, onClose } = props;
  const isMobile = useIsMobile();

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(5),
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(4),
        borderBottom: `1px solid ${theme.palette.border?.base}`,
        overflow: "hidden",
        flexShrink: 0,
      })}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Text
          bold
          sx={(theme) => ({
            whiteSpace: "normal",
            color: theme.palette.base?.strong,
          })}
        >
          {title}
        </Text>
      </Box>

      {showCloseButton && (
        <IconButton
          circular
          aria-label={tu("Close dialog")}
          iconType="close"
          size={isMobile ? "medium" : "small"}
          variant="outlined"
          onClick={onClose}
        />
      )}
    </Box>
  );
}
